// frontend/src/features/regions/components/RegionsInactiveClientsChart.tsx
import React from 'react';
import {
    BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList
} from 'recharts';

interface InactiveClient {
    name: string;
    ultimaCompra: string;
    totalHistorico: number;
    uf: string;
    cidade?: string;
    ibge?: number | null;
}

interface RegionsInactiveClientsChartProps {
    data: InactiveClient[];
    onClientClick?: (client: InactiveClient) => void;
    selectedClientName?: string | null;
}

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const BAR_COLOR = 'var(--theme-accent, #1e293b)'; // theme accent

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency', currency: 'BRL',
        notation: 'compact', compactDisplay: 'short',
    }).format(value);

const BarTick = (props: any) => {
    const { x, y, payload } = props;
    const raw = String(payload.value ?? '');
    const maxLen = 18;
    const display = raw.length > maxLen ? raw.substring(0, maxLen - 2) + '…' : raw;
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={-8}
                y={0}
                dy="0.35em"
                textAnchor="end"
                fontSize={11}
                fontWeight={700}
                fill="var(--text-primary)"
                style={{ textTransform: 'uppercase', fontFamily: FONT }}
            >
                {display}
            </text>
        </g>
    );
};

const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as InactiveClient;
    
    const daysSince = Math.floor(
        (new Date().getTime() - new Date(d.ultimaCompra).getTime()) / (1000 * 60 * 60 * 24)
    );

    return (
        <div style={{
            background: 'var(--bg-widget)', borderRadius: 'var(--radius-base, 8px)', padding: '10px 14px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)', border: '1px solid var(--border-primary)',
            fontSize: 13, maxWidth: 280, fontFamily: FONT,
        }}>
            <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px', fontSize: 14,
                borderBottom: '1px solid var(--border-primary)', paddingBottom: 6 }}>
                {d.name}
            </p>
            {[
                { label: 'Última Compra', value: `há ${daysSince} ${daysSince === 1 ? 'dia' : 'dias'}`, color: '#ef4444' },
                { label: 'Histórico', value: formatCurrency(d.totalHistorico), color: 'var(--text-primary)' },
                { label: 'Região (UF)', value: d.uf, color: 'var(--text-primary)' },
            ].map(({ label, value, color }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between',
                    gap: 16, padding: '2px 0' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
                    <span style={{ fontWeight: 700, color }}>{value}</span>
                </div>
            ))}
        </div>
    );
};

const RegionsInactiveClientsChart: React.FC<RegionsInactiveClientsChartProps> = ({ data, onClientClick, selectedClientName }) => {
    if (!data?.length) {
        return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)',
            fontWeight: 600, fontSize: 13, fontFamily: FONT }}>Nenhum cliente inativo no período.</div>;
    }

    const chartData = [...data]
        .sort((a, b) => b.totalHistorico - a.totalHistorico)
        .slice(0, 10);


    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 4, right: 130, left: 0, bottom: 4 }}
                barCategoryGap="25%"
            >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border-primary)" />
                <XAxis type="number" hide domain={[0, (max: number) => max * 1.35]} />
                <YAxis
                    dataKey="name"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    width={160}
                    tick={<BarTick />}
                />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                />
                <Bar
                    dataKey="totalHistorico"
                    radius={[0, 0, 0, 0]}
                    barSize={16}
                    style={{ cursor: 'pointer' }}
                >
                    {chartData.map((entry, index) => {
                        const isSelected = selectedClientName === entry.name;
                        const fill = isSelected ? 'var(--theme-primary, #b91c1c)' : BAR_COLOR;
                        return (
                            <Cell
                                key={`cell-${index}`}
                                fill={fill}
                                onClick={() => onClientClick?.(entry)}
                            />
                        );
                    })}
                    <LabelList
                        dataKey="totalHistorico"
                        position="right"
                        content={({ x, y, width, height: h, value }: any) => (
                            <text
                                x={(x as number) + (width as number) + 8}
                                y={(y as number) + (h as number) / 2}
                                textAnchor="start"
                                dominantBaseline="central"
                                fontSize={13}
                                fontWeight={700}
                                fill="var(--text-primary)"
                                style={{ pointerEvents: 'none', fontFamily: FONT }}
                            >
                                {formatCurrency(value)}
                            </text>
                        )}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default RegionsInactiveClientsChart;
